import React from 'react';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import Box from '../Atoms/Box';

interface CustomCheckboxProps extends Omit<CheckboxProps, 'sx'> {
  /**
   * Optional override for checkbox sizing
   */
  checkboxSize?: 'small' | 'medium' | 'large';

  /**
   * Optional wrapper to center the checkbox
   */
  centered?: boolean;

  /**
   * Optional aria-label for accessibility
   */
  ariaLabel?: string;
}

/**
 * CustomCheckbox component that provides consistent styling across the application
 * with enhanced accessibility features
 */
const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  checkboxSize = 'medium',
  centered = false,
  ariaLabel,
  ...props
}) => {
  // Determine sizing based on the checkboxSize prop
  const sizeConfig = {
    small: {
      size: 'small' as const,
      width: '24px',
      height: '24px',
      iconSize: '20px',
    },
    medium: {
      size: 'medium' as const,
      width: '28px',
      height: '28px',
      iconSize: '24px',
    },
    large: {
      size: 'medium' as const,
      width: '32px',
      height: '32px',
      iconSize: '28px',
    },
  };

  const currentSize = sizeConfig[checkboxSize];

  const checkbox = (
    <Checkbox
      {...props}
      size={currentSize.size}
      inputProps={{
        ...props.inputProps,
        'aria-label': ariaLabel || 'Select item',
      }}
      sx={{
        padding: '4px',
        '&.MuiCheckbox-root': {
          width: currentSize.width,
          height: currentSize.height,
        },
        '& .MuiSvgIcon-root': {
          fontSize: currentSize.iconSize,
        },
        color: 'primary.main',
        '&.Mui-checked': {
          color: 'primary.main',
        },
        '&.Mui-focused': {
          outline: '2px solid #4786FC',
          borderRadius: '4px',
        },
      }}
    />
  );

  // If centered, wrap in a Box for centering
  if (centered) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          margin: '0 auto',
          padding: 0,
        }}
      >
        {checkbox}
      </Box>
    );
  }

  return checkbox;
};

export default CustomCheckbox;
